import { Fragment } from 'react';

import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubCard,
  DevHubGrid,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { ApiPortalData } from './ApiPortalTypes';
import { GET_API_PORTAL_ORGS_PAGED } from './ApiPortalQueries';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Content, Header, Page, Progress } from '@backstage/core-components';
import { ConfigHelper } from '@devxdiscover/devhub-common';

/**
 * A component to render the API Portal page.
 */
export const ApiPortals = () => {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery<ApiPortalData>(
    GET_API_PORTAL_ORGS_PAGED,
  );

  const approvedApiPortals =
    ConfigHelper.instance.config
      .getOptionalString('app.approvedApiPortals')
      ?.split(',') || [];

  const handleCardClick = (orgNormalizedName: string) => {
    navigate(`/api-portals/${orgNormalizedName}`);
  };

  return (
    <Page themeId="home">
      <Header
        style={{ whiteSpace: 'nowrap' }}
        title="API Portals"
        subtitle="Explore the APIs and Data Feeds that power our ecosystem"
      />
      <Content>
        <DevHubBox
          tbPadding="large"
          display="flex"
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
        >
          <DevHubBox
            tbPadding="large"
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
            bgcolor="background.paper"
            borderRadius="borderRadius"
            boxShadow={3}
            p={3}
          >
            <DevHubTypography fontColor="blue" variant="h4" gutterBottom>
              Welcome to DevHub
            </DevHubTypography>
            <DevHubTypography variant="body1" align="center">
              The place to find Connected Living APIs, Scenario Documents, and
              Data Feed specifications. This new portal contains the same API
              specs and scenario documents as found on the previous Dev Portal.
              The DevHub API Portal is still a work in progress –- and the team
              is actively working to add new features and functionality.
            </DevHubTypography>
          </DevHubBox>
        </DevHubBox>
        {loading && <Progress />}
        {error && (
          <DevHubAlert severity="error">
            Error loading API Portal organizations
          </DevHubAlert>
        )}
        <DevHubGrid container spacing={2}>
          {data?.devhub?.apiPortal?.apiPortalOrgsPaged?.orgs
            .filter(org => approvedApiPortals.includes(org._normalizedName))
            .slice()
            .sort((a, b) => a._normalizedName.localeCompare(b._normalizedName))
            .map(org => (
              <DevHubGrid item xs={12} md={6} lg={4} key={org.name}>
                <DevHubCard
                  density="compact"
                  consistentHeight
                  topBorder="custom"
                  topBorderColor="blue"
                  bodyPosition="middle"
                  subTitle=""
                  body={
                    <Fragment>
                      <DevHubBox
                        tbPadding="large"
                        display="flex"
                        justifyContent="center"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <DevHubTypography variant="h5">
                          {org.name
                            .split('-')
                            .map(
                              word =>
                                word.charAt(0).toUpperCase() + word.slice(1),
                            )
                            .join(' ')}
                        </DevHubTypography>
                        <DevHubTypography>
                          Everything You Need To Build Incredible Experiences
                        </DevHubTypography>
                      </DevHubBox>
                      <DevHubBox
                        tbPadding="large"
                        display="flex"
                        justifyContent="center"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <DevHubButton
                          variant="contained"
                          onClick={() => handleCardClick(org._normalizedName)}
                        >
                          Explore this API Portal
                        </DevHubButton>
                      </DevHubBox>
                    </Fragment>
                  }
                />
              </DevHubGrid>
            ))}
        </DevHubGrid>
      </Content>
    </Page>
  );
};
